// Location detection utilities
export interface LocationInfo {
  locationName: string
  country: string
  city: string
  region: string
}

// Function to detect user's location using browser's geolocation API
export async function detectUserLocation(): Promise<LocationInfo> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      // Fallback to IP-based location detection
      detectLocationByIP()
        .then(resolve)
        .catch(() => {
          // Final fallback to default location
          resolve({
            locationName: 'Unknown',
            country: 'Unknown',
            city: 'Unknown',
            region: 'Unknown',
          })
        })
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const {latitude, longitude} = position.coords
          const locationInfo = await reverseGeocode(latitude, longitude)
          resolve(locationInfo)
        } catch (error) {
          // Fallback to IP-based detection
          detectLocationByIP()
            .then(resolve)
            .catch(() => {
              resolve({
                locationName: 'Unknown',
                country: 'Unknown',
                city: 'Unknown',
                region: 'Unknown',
              })
            })
        }
      },
      (error) => {
        console.warn('Geolocation error:', error)
        // Fallback to IP-based detection
        detectLocationByIP()
          .then(resolve)
          .catch(() => {
            resolve({
              locationName: 'Unknown',
              country: 'Unknown',
              city: 'Unknown',
              region: 'Unknown',
            })
          })
      },
      {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000, // 5 minutes
      }
    )
  })
}

// Function to reverse geocode coordinates to get location information
async function reverseGeocode(latitude: number, longitude: number): Promise<LocationInfo> {
  try {
    // Using a free reverse geocoding service
    const response = await fetch(
      'https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en'
    )

    if (!response.ok) {
      throw new Error('Reverse geocoding failed')
    }

    const data = await response.json()

    return {
      locationName: data.locality || data.principalSubdivision || data.countryName || 'Unknown',
      country: data.countryName || 'Unknown',
      city: data.city || data.locality || 'Unknown',
      region: data.principalSubdivision || 'Unknown',
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error)
    throw error
  }
}

// Function to detect location using IP address
async function detectLocationByIP(): Promise<LocationInfo> {
  try {
    const response = await fetch('https://ipapi.co/json/')

    if (!response.ok) {
      throw new Error('IP-based location detection failed')
    }

    const data = await response.json()

    return {
      locationName: data.city || data.region || data.country_name || 'Unknown',
      country: data.country_name || 'Unknown',
      city: data.city || 'Unknown',
      region: data.region || 'Unknown',
    }
  } catch (error) {
    console.error('IP-based location detection error:', error)
    throw error
  }
}

// Function to get a simplified location name for the API
export async function getLocationName(): Promise<string> {
  try {
    const locationInfo = await detectUserLocation()

    // Return the most specific location name available
    if (locationInfo.city && locationInfo.city !== 'Unknown') {
      return `${locationInfo.city}, ${locationInfo.country}`
    } else if (locationInfo.region && locationInfo.region !== 'Unknown') {
      return `${locationInfo.region}, ${locationInfo.country}`
    } else if (locationInfo.country && locationInfo.country !== 'Unknown') {
      return locationInfo.country
    } else {
      return 'Unknown Location'
    }
  } catch (error) {
    console.error('Error getting location name:', error)
    return 'Unknown Location'
  }
}
